import { useCallback, useEffect, useState, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { X } from 'react-feather'
import Button from 'antd/lib/button'

import {
  Modal,
  useProjectDetails,
  useShowMessage,
  usePaginatedAssignmentList,
  useCompanyDetails,
  useVerticalMappingJson,
} from '@pepper/ui'

import { dataLayerDuplicateProjectDraftCreatedEvent } from '@pepper/utils/src/function/dataLayerEvents'
import { createProject as createProjectDraft } from '@pepper/utils/src/api/projects/projects'
import { getCreateProjectJSON } from '@pepper/utils/src/api/utils/utils'
import { fetchDetails } from '@pepper/utils/src/api/assignments/assignments'
import { verticalMap } from '@pepper/utils/src/function/verticalMap'
import getFormFields from '@pepper/utils/src/function/getFormFields'
import { getTagSetFromGroupedKey } from '@pepper/utils/src/function/getTagsetFromGroupKey'
import transformFields from '@pepper/utils/src/function/transformFields'
import { onlyUnique } from '@pepper/utils/src/function/misc'

import {
  useSelectedCompanyId,
  filterCreateProjectJsonByAccountType,
  getDraftBodyFromProjectDetails,
} from 'utils/functions/misc'
import useUserAccountType from 'utils/hooks/useUserAccountType'

import styles from './_.module.css'

type ConfirmProjectDuplicationModalProps = {
  projectId: string
}

const getLatestItem = <T extends Record<string, unknown>>(
  items: T[],
  keyForTime: string
): T => {
  let latestCreationDate = new Date(items[0][keyForTime] as string)
  let latestItem = items[0]
  items.forEach(item => {
    const assignmentCreationDate = new Date(item[keyForTime] as string)
    if (assignmentCreationDate > latestCreationDate) {
      latestCreationDate = assignmentCreationDate
      latestItem = item
    }
  })
  return latestItem
}

const getAssignmentForPreFillingProjectDetails = (
  assignments: Assignment[],
  verticalIds: number | number[],
  groupedVerticalIdsByGroupId?: Record<string, number[]>
) => {
  if (Array.isArray(verticalIds) && groupedVerticalIdsByGroupId) {
    const selectedGroupIds = Object.keys(groupedVerticalIdsByGroupId)
      .filter(
        groupId =>
          groupedVerticalIdsByGroupId[groupId].join(',') ===
          verticalIds.join(',')
      )
      .map(groupId => Number(groupId))
    const assignmentsBySelectedGroupIds = selectedGroupIds.reduce(
      (acc: Record<number, Assignment[]>, groupId) => {
        acc[groupId] = assignments.filter(
          assignment => assignment.group_id === groupId
        )
        return acc
      },
      {}
    )

    // We can safely assume that there is atlease one assignment for each group
    const latestAssignment = getLatestItem<Assignment>(
      Object.values(assignmentsBySelectedGroupIds).map(
        assignments => assignments[0]
      ),
      'date_created'
    )
    const latestGroupIdInSelectedGroupIds = Number(latestAssignment.group_id)
    const _relevantAssignments =
      assignmentsBySelectedGroupIds[latestGroupIdInSelectedGroupIds]
    // Here we are rearranging the assignments with the assignment belonging to the root vertical in first place
    let rootAssignmentIndex
    _relevantAssignments.some((a, index) => {
      if (
        a.vertical.id ===
        (Array.isArray(verticalIds) ? verticalIds[0] : verticalIds)
      ) {
        rootAssignmentIndex = index
        return true
      }
      return false
    })
    const rootAssignment = _relevantAssignments.splice(
      rootAssignmentIndex as unknown as number,
      1
    )[0]
    _relevantAssignments.unshift(rootAssignment)
    return _relevantAssignments
  }

  const _assignmentsForVertical = assignments.filter(
    assignment => assignment.vertical.id === verticalIds
  )
  // We can safely assume that there is atlease one assignment for each vertical
  return [getLatestItem<Assignment>(_assignmentsForVertical, 'date_created')]
}

const supportedVerticalIdsMappingForGroupedAssignment = (
  verticalMappingJson: IVerticalMapping
): string[] => {
  return Object.values(verticalMappingJson).map(rootVertical => {
    return [
      rootVertical.id,
      ...rootVertical.mappedVerticals.map(vertical => vertical.id),
    ].join(',')
  })
}

const ConfirmProjectDuplicationModal =
  NiceModal.create<ConfirmProjectDuplicationModalProps>(({ projectId }) => {
    const modal = useModal()
    const showMessage = useShowMessage()
    const history = useHistory()
    const companyId = useSelectedCompanyId()
    const accountType = useUserAccountType()
    const {
      data: verticalMappingJson,
      isSuccess: isVerticalMappingJsonFetched,
    } = useVerticalMappingJson()

    const {
      show: showServiceSelectionModal,
      remove: removeServiceSelectionModal,
    } = useModal('project-duplication-service-selection')

    const { data: projectDetails } = useProjectDetails(projectId)
    const { records: assignments } = usePaginatedAssignmentList(
      {
        project_id: projectId,
      },
      undefined,
      false
    )
    const { data: company } = useCompanyDetails(
      `/companies/${companyId}`,
      companyId
    )

    const [
      createProjectJsonAndAssignmentDetails,
      setCreateProjectJsonAndAssignmentDetails,
    ] = useState<{
      assignmentsDetails: Assignment[] | undefined
      fieldsFromJson: Record<string, any>[]
      keySetContainingTagField: Record<
        string,
        {
          tag: string
          tagField: string
          isAssignment?: boolean
        }
      >
      rootVerticalId?: number
    }>({
      assignmentsDetails: undefined,
      fieldsFromJson: [],
      keySetContainingTagField: {},
    })
    const [isSubmitButtonInLoadingState, setIsSubmitButtonInLoadingState] =
      useState(false)

    const supportedGroupedVerticalIdsMapping = useMemo(() => {
      if (isVerticalMappingJsonFetched && verticalMappingJson)
        return supportedVerticalIdsMappingForGroupedAssignment(
          verticalMappingJson.response
        )
      return []
    }, [isVerticalMappingJsonFetched, verticalMappingJson])

    const [verticalIdsList, groupedVerticalIdsByGroupId] = useMemo(() => {
      if (!assignments) return [[], {}]

      const ungroupedAssignments = assignments.filter(
        assignment =>
          assignment.group_count === 1 || assignment.group_count === undefined
      )
      const ungroupedVerticalIds = [
        ...new Set(
          ungroupedAssignments.map(assignment => assignment.vertical.id)
        ),
      ]

      const groupedAssignments = assignments.filter(
        assignment => assignment.group_count && assignment.group_count > 1
      )
      const groupedAssignmentsByGroupId = groupedAssignments.reduce(
        (acc: Record<string, Assignment[]>, assignment) => {
          if (acc[assignment.group_id ?? '']) {
            acc[assignment.group_id ?? ''].push(assignment)
          } else {
            acc[assignment.group_id ?? ''] = [assignment]
          }
          return acc
        },
        {}
      )

      const groupedVerticalIdsByGroupId: Record<string, number[]> = {}
      Object.keys(groupedAssignmentsByGroupId).forEach(groupId => {
        const _groupedAssignments = groupedAssignmentsByGroupId[groupId]
        groupedVerticalIdsByGroupId[groupId] = _groupedAssignments.reduce(
          (verticalIds: number[], assignment) => {
            if (verticalIds.includes(assignment.vertical.id)) return verticalIds
            verticalIds.push(assignment.vertical.id)
            return verticalIds
          },
          []
        )

        // Here we are rearranging the assignments with the assignment belonging to the root vertical in first place
        const rootVertical = supportedGroupedVerticalIdsMapping
          .find(groupedVerticalIds => {
            return (
              groupedVerticalIds
                .split(',')
                .map(_ => Number(_))
                .sort()
                .join(',') ===
              groupedVerticalIdsByGroupId[groupId].sort().join(',')
            )
          })
          ?.split(',')[0]
        let rootAssignmentIndex
        _groupedAssignments.some((a, index) => {
          if (a.vertical.id === Number(rootVertical)) {
            rootAssignmentIndex = index
            return true
          }
          return false
        })
        const rootAssignment = _groupedAssignments.splice(
          rootAssignmentIndex as unknown as number,
          1
        )[0]
        _groupedAssignments.unshift(rootAssignment)

        groupedVerticalIdsByGroupId[groupId] = _groupedAssignments.reduce(
          (verticalIds: number[], assignment) => {
            // There can be multiple translation assignment in one group
            if (verticalIds.includes(assignment.vertical.id)) return verticalIds
            verticalIds.push(assignment.vertical.id)
            return verticalIds
          },
          []
        )
        groupedAssignmentsByGroupId[groupId] = _groupedAssignments
      })

      const uniqueGroupedVerticalIds = Object.values(
        groupedVerticalIdsByGroupId
      )
        .map(_ => _.join(','))
        .filter(groupedId => {
          if (!verticalMappingJson) return true
          return supportedGroupedVerticalIdsMapping.includes(groupedId)
        })
        .filter(onlyUnique)
        .map(_ => _.split(',').map(_ => Number(_)))

      return [
        [...ungroupedVerticalIds, ...uniqueGroupedVerticalIds],
        groupedVerticalIdsByGroupId,
      ]
    }, [assignments, verticalMappingJson, supportedGroupedVerticalIdsMapping])

    const isMultiCategoryProject = useMemo(() => {
      return verticalIdsList.length > 1
    }, [verticalIdsList.length])

    const fetchProjectJsonAndData = useCallback(
      async (assignments: Assignment[], rootVerticalId: number) => {
        const [createProjectJsonResults, assignmentsDetailsResults] =
          await Promise.allSettled([
            Promise.all(
              assignments.map(assignment =>
                getCreateProjectJSON(
                  assignment.vertical.id,
                  assignment.json_version
                )
              )
            ),
            Promise.all(
              assignments.map(assignment =>
                fetchDetails({
                  id: assignment.id,
                })
              )
            ),
          ])

        if (
          createProjectJsonResults.status === 'rejected' ||
          assignmentsDetailsResults.status === 'rejected'
        ) {
          showMessage({
            type: 'error',
            message: 'Error in fetching project data.',
          })
          return
        }
        const areAllCreateProjectJsonsFetched =
          createProjectJsonResults.value.every(response => response.data)
        const areAllAssignmentDetailsFetched =
          assignmentsDetailsResults.value.every(
            response => response.status === 'ok' && response.data
          )
        if (
          !areAllCreateProjectJsonsFetched ||
          !areAllAssignmentDetailsFetched
        ) {
          showMessage({
            type: 'error',
            message: 'Error in fetching project data.',
          })
          return
        }

        let completeVerticalJson
        if (assignments.length > 1) {
          const [rootVerticalJson, mappedVerticalJson] =
            createProjectJsonResults.value.map((response, index) =>
              filterCreateProjectJsonByAccountType(
                {
                  ...response.data,
                  category: assignments[index].category.id,
                  id: assignments[index].vertical.id,
                },
                accountType ?? ''
              )
            )
          const { completeVerticalData } = verticalMap(
            rootVerticalJson,
            mappedVerticalJson,
            accountType,
            1
          )
          completeVerticalJson = completeVerticalData
        } else {
          completeVerticalJson = filterCreateProjectJsonByAccountType(
            createProjectJsonResults.value[0].data,
            accountType ?? ''
          )
        }

        const keySetContainingTagField = getTagSetFromGroupedKey({
          verticalResponse: completeVerticalJson,
        })
        setCreateProjectJsonAndAssignmentDetails({
          assignmentsDetails: assignmentsDetailsResults.value.map(
            response => response.data as Assignment
          ),
          fieldsFromJson: getFormFields(completeVerticalJson.response, {
            shouldIncludeAssignmentLevelKeys: false,
            shouldRemoveKeysWithoutDisplayType: false,
          }),
          keySetContainingTagField,
          rootVerticalId,
        })
      },
      [accountType, showMessage]
    )

    const handleMultiCategoryProjectDuplication = useCallback(async () => {
      setIsSubmitButtonInLoadingState(true)
      const verticalIdsSelectedByUser = (await showServiceSelectionModal({
        serviceIdsList: verticalIdsList,
        closeDuplicateConfirmationModal: modal.remove,
      })) as unknown as number | number[]
      const _assignments = getAssignmentForPreFillingProjectDetails(
        assignments,
        verticalIdsSelectedByUser,
        groupedVerticalIdsByGroupId
      )
      fetchProjectJsonAndData(
        _assignments,
        Array.isArray(verticalIdsSelectedByUser)
          ? verticalIdsSelectedByUser[0]
          : verticalIdsSelectedByUser
      )
    }, [
      assignments,
      fetchProjectJsonAndData,
      showServiceSelectionModal,
      verticalIdsList,
      modal.remove,
      groupedVerticalIdsByGroupId,
    ])

    const handleProjectDuplicationConfirmation = useCallback(async () => {
      if (
        !company ||
        !projectDetails ||
        !assignments ||
        assignments.length === 0 ||
        !createProjectJsonAndAssignmentDetails.assignmentsDetails ||
        createProjectJsonAndAssignmentDetails.fieldsFromJson.length === 0
      ) {
        setIsSubmitButtonInLoadingState(true)
        return
      }

      const {
        status,
        error,
        data: draftCreationResponse,
      } = await createProjectDraft({
        body: getDraftBodyFromProjectDetails({
          projectDetails,
          companyId,
          assignments: createProjectJsonAndAssignmentDetails.assignmentsDetails,
          company,
          fields: transformFields(
            createProjectJsonAndAssignmentDetails.fieldsFromJson
          ),
          keySetContainingTagField:
            createProjectJsonAndAssignmentDetails.keySetContainingTagField,
          rootVerticalId: createProjectJsonAndAssignmentDetails.rootVerticalId,
        }),
        type: 'draft',
      })
      if (status === 'ok' && draftCreationResponse !== null) {
        dataLayerDuplicateProjectDraftCreatedEvent({
          draftId: draftCreationResponse.id,
          originalProjectId: projectId,
        })
        history.push(
          `/create-project-flow?draft_id=${draftCreationResponse.id}`
        )
        removeServiceSelectionModal()
        modal.remove()
      } else {
        showMessage({ type: 'error', message: error })
      }
    }, [
      assignments,
      company,
      companyId,
      createProjectJsonAndAssignmentDetails,
      history,
      modal,
      projectDetails,
      projectId,
      removeServiceSelectionModal,
      showMessage,
    ])

    useEffect(() => {
      // this means duplicate project button was clicked and data wasn't ready, but is ready now
      if (
        isSubmitButtonInLoadingState &&
        projectDetails &&
        assignments?.length > 0 &&
        createProjectJsonAndAssignmentDetails.assignmentsDetails &&
        createProjectJsonAndAssignmentDetails.fieldsFromJson.length > 0
      ) {
        handleProjectDuplicationConfirmation()
        setIsSubmitButtonInLoadingState(false)
      }
    }, [
      handleProjectDuplicationConfirmation,
      isSubmitButtonInLoadingState,
      createProjectJsonAndAssignmentDetails,
      projectDetails,
      assignments,
    ])

    useEffect(() => {
      if (!assignments || !projectDetails || assignments.length === 0) {
        return
      }
      if (
        createProjectJsonAndAssignmentDetails.assignmentsDetails &&
        createProjectJsonAndAssignmentDetails.fieldsFromJson.length > 0
      ) {
        return
      }

      if (isMultiCategoryProject) {
        // We can't fetch the data yet as user need to select the vertical
        return
      }

      if (Array.isArray(verticalIdsList[0])) {
        // Only grouped assignments of same type in the project
        const _assignments = getAssignmentForPreFillingProjectDetails(
          assignments,
          verticalIdsList[0],
          groupedVerticalIdsByGroupId
        )
        fetchProjectJsonAndData(_assignments, verticalIdsList[0][0])
      } else {
        // Normal legacy project, just use the first assignment for prefilling
        fetchProjectJsonAndData([assignments[0]], verticalIdsList[0])
      }
    }, [
      assignments,
      createProjectJsonAndAssignmentDetails.assignmentsDetails,
      createProjectJsonAndAssignmentDetails.fieldsFromJson.length,
      fetchProjectJsonAndData,
      groupedVerticalIdsByGroupId,
      isMultiCategoryProject,
      projectDetails,
      verticalIdsList,
    ])

    return (
      <Modal
        wrapClassName={styles.modalWrapper}
        title={<span className={styles.modalTitle}>Duplicate the Project</span>}
        okText="Duplicate Project"
        onCancel={modal.remove}
        visible={modal.visible}
        closable={true}
        closeIcon={<X size={16} />}
        footer={[
          <Button
            key="cancel"
            type="link"
            className={styles.cancelButton}
            onClick={modal.remove}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            className={styles.submitButton}
            onClick={
              isMultiCategoryProject
                ? handleMultiCategoryProjectDuplication
                : handleProjectDuplicationConfirmation
            }
            loading={isSubmitButtonInLoadingState}
            data-id="business-confirm-project-duplication-modal-duplicate-project-button"
          >
            Duplicate Project
          </Button>,
        ]}
      >
        Duplicating this project will make a copy of the project brief. You will
        be redirected to the project creation flow.
      </Modal>
    )
  })

export default ConfirmProjectDuplicationModal
